import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm, Controller, type SubmitHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useAuth } from '../../hooks/useAuth';
import type { LoginCredentials } from '../../types/auth.types';
import { loginSchema, type LoginFormInputs } from '../../schemas/auth.schemas';
import { Form, Input, Label, Checkbox } from '@/shared/components/form';
import { Button } from '@/shared/components/ui/button/Button';
import { EyeIcon, EyeOff } from 'lucide-react';

interface LoginFormProps {
    onError?: (error: string) => void;
    className?: string;
}

export const LoginForm: React.FC<LoginFormProps> = ({ onError }) => {
    const { login, isLoading, clearError } = useAuth();
    const {
        control,
        handleSubmit,
        formState: { errors },
        setError,
    } = useForm<LoginFormInputs>({
        resolver: zodResolver(loginSchema),
        defaultValues: {
            email: '',
            password: '',
            remember_me: false,
        },
    });
    const [showPassword, setShowPassword] = useState(false);

    const onSubmit: SubmitHandler<LoginFormInputs> = async (data) => {
        clearError();

        try {
            const credentials: LoginCredentials = {
                email: data.email,
                password: data.password,
                remember_me: data.remember_me,
            };
            await login(credentials);

            // Don't call onSuccess here - let AuthRoutes handle the routing
            // based on the authentication state (mfaStatus, isAuthenticated, etc.)
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Login failed';
            onError?.(errorMessage);
            setError('root', { message: errorMessage });
        }
    };

    return (
        <div className="flex flex-col flex-1">
            <div className="flex flex-col justify-center flex-1 w-full max-w-md mx-auto">
                <div>
                    <div className="mb-5 sm:mb-8">
                        <h1 className="hidden mb-2 font-semibold text-mp-foreground text-title-sm sm:text-title-md lg:block">
                            Sign In
                        </h1>
                        <p className="text-sm text-mp-muted">
                            Enter your email and password to sign in!
                        </p>
                    </div>
                    <div>
                        <Form onSubmit={handleSubmit(onSubmit)}>
                            <div className="space-y-6">
                                <div>
                                    <Label>
                                        Email <span className="text-mp-error">*</span>{' '}
                                    </Label>
                                    <Controller
                                        name="email"
                                        control={control}
                                        render={({ field, fieldState }) => (
                                            <Input
                                                type="text"
                                                id="email"
                                                placeholder="Enter your email address"
                                                value={field.value ?? ''}
                                                onChange={field.onChange}
                                                error={!!fieldState.error}
                                                hint={fieldState.error?.message}
                                            />
                                        )}
                                    />
                                </div>
                                <div>
                                    <Label>
                                        Password <span className="text-mp-error">*</span>{' '}
                                    </Label>
                                    <div className="relative">
                                        <Controller
                                            name="password"
                                            control={control}
                                            render={({ field, fieldState }) => (
                                                <Input
                                                    type={showPassword ? 'text' : 'password'}
                                                    id="password"
                                                    placeholder="Enter your password"
                                                    value={field.value ?? ''}
                                                    onChange={field.onChange}
                                                    error={!!fieldState.error}
                                                    hint={fieldState.error?.message}
                                                >
                                                    <span
                                                        onClick={() =>
                                                            setShowPassword(!showPassword)
                                                        }
                                                        className="absolute z-30 -translate-y-1/2 cursor-pointer right-4 top-1/2"
                                                    >
                                                        {showPassword ? (
                                                            <EyeIcon
                                                                size={21}
                                                                className="text-mp-muted"
                                                            />
                                                        ) : (
                                                            <EyeOff
                                                                size={21}
                                                                className="text-mp-muted"
                                                            />
                                                        )}
                                                    </span>
                                                </Input>
                                            )}
                                        />
                                    </div>
                                </div>
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <Controller
                                            name="remember_me"
                                            control={control}
                                            render={({ field }) => (
                                                <Checkbox
                                                    id="remember_me"
                                                    ariaLabel="Keep me logged in"
                                                    checked={field.value ?? false}
                                                    onChange={field.onChange}
                                                />
                                            )}
                                        />
                                        <span className="block font-normal text-mp-foreground text-theme-sm">
                                            Keep me logged in
                                        </span>
                                    </div>
                                    <Link
                                        to="/password/forgot"
                                        className="text-sm text-mp-link"
                                    >
                                        Forgot password?
                                    </Link>
                                </div>
                                <div>
                                    <Button disabled={isLoading} className="w-full" size="sm">
                                        {isLoading ? 'Signing In...' : 'Sign In'}
                                    </Button>
                                </div>
                                {errors.root && (
                                    <p className="text-sm text-center text-mp-error">
                                        {errors.root.message}
                                    </p>
                                )}
                            </div>
                        </Form>

                        <div className="mt-5">
                            <p className="text-sm font-normal text-center text-mp-foreground sm:text-start">
                                Don&apos;t have an account? {''}
                                <Link
                                    to="/signup"
                                    className="text-mp-link"
                                >
                                    Sign Up
                                </Link>
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
